import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useApp, localISO } from "@/context/AppContext";
import { Card } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ClipboardList, Save } from "lucide-react";
import { ComplaintButton, SuggestionButton, UnhappyButton } from "@/components/QuickActions";
import { toast } from "sonner";

const EXE = [["calls_made", "Calls Made"], ["followup_calls", "Follow-up Calls"], ["calls_connected", "Connected"],
  ["calls_not_connected", "Not Connected"], ["due_recovered", "Due Recovered ₹"], ["due_pending", "Due Pending ₹"],
  ["patients_with_dues", "Patients w/ Dues"], ["revenue_generated", "Revenue ₹"], ["revisit_patients", "Revisit"],
  ["referrals_count", "Referrals"], ["feedback_count", "Feedback"], ["reviews_count", "Reviews"],
  ["testimonials_count", "Testimonials"], ["concerns_recorded", "Concerns Recorded"], ["upsell_count", "Upsell #"],
  ["upsell_amount", "Upsell ₹"]];
const EXE_SAT = [["happy", "Happy"], ["satisfied", "Satisfied"], ["unhappy", "Unhappy"]];

const MGR_COUNTS = [["patients_visited", "Total Patients Visited"], ["new_patients", "New Patients"],
  ["wanted_treatment", "Wanted Treatment"], ["took_treatment", "Took Treatment"], ["total_counselled", "Total Counselled"],
  ["referrals_count", "Referrals"], ["cross_sell_amount", "Cross-sell ₹"], ["reviews_count", "Reviews"], ["testimonials_count", "Testimonials"]];
const MGR_LF = [["lf_tv", "TV"], ["lf_music", "Music"], ["lf_fragrance", "Fragrance"], ["lf_display", "Display"], ["lf_cleanliness", "Cleanliness"], ["lf_activity", "Activity"]];
const MGR_FB = [["fb_doctor", "Doctor"], ["fb_reception", "Reception"], ["fb_ot", "OT Staff"], ["fb_hygiene", "Hygiene"], ["fb_overall", "Overall"]];
const MGR_SUG = [["suggestions_received", "Received"], ["suggestions_implemented", "Implemented"], ["suggestions_pending", "Pending"]];

const pct = (a, b) => { a = Number(a) || 0; b = Number(b) || 0; return b ? Math.round((100 * a) / b) : 0; };
const sumF = (form, list) => list.reduce((s, [k]) => s + (Number(form[k]) || 0), 0);

function NumGrid({ list, form, set, max }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
      {list.map(([k, label]) => (
        <div key={k}>
          <label className="text-[11px] text-slate-400 block mb-1">{label}</label>
          <Input data-testid={`dw-${k}`} type="number" min={0} max={max} value={form[k] ?? ""} onChange={(e) => set(k, e.target.value)}
            className="bg-[#0F172A] border-white/10 text-white font-mono-ync h-9" />
        </div>
      ))}
    </div>
  );
}

export default function DailyWorkCard() {
  const { person, currentRole, team } = useApp();
  const [form, setForm] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [complaints, setComplaints] = useState({ received: 0, resolved: 0, open: 0 });
  const [unhappyReasons, setUnhappyReasons] = useState(0);
  const today = localISO();
  const [workDate, setWorkDate] = useState(today);
  const role = currentRole;
  const isMgr = role === "Operational Manager" || role === "Manager";
  const isExe = role === "Relationship Executive";
  const member = team.find((t) => t.name === person);
  const branch = member?.branch || member?.branches?.[0] || "";

  const load = useCallback(async () => {
    if (!person || (!isMgr && !isExe)) return;
    try {
      const res = await api.get("/dailywork", { params: { person, date_from: workDate, date_to: workDate, date_field: "work_date" } });
      const rec = (res.data || []).find((r) => r.person === person && (r.branch || "") === branch);
      if (rec) { setForm(rec); setEditingId(rec.id); }
      else { setForm({}); setEditingId(null); }
    } catch {}
    if (isMgr) {
      try {
        const c = await api.get("/concerns", branch ? { params: { branch } } : {});
        const rows = c.data || [];
        const resolved = rows.filter((x) => ["Resolved", "Closed"].includes(x.status)).length;
        setComplaints({ received: rows.length, resolved, open: rows.length - resolved });
      } catch {}
    }
    if (isExe) {
      try {
        const c = await api.get("/concerns", { params: { person } });
        const rows = (c.data || []).filter((x) => x.concern_type === "Unhappy Reason");
        setUnhappyReasons(rows.length);
      } catch {}
    }
  }, [person, role, workDate, branch]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  if (!person) return (
    <Card className="p-4 mb-5 border-dashed"><p className="text-sm text-slate-400" data-testid="dailywork-select-name">Apna naam header me select karo (right side) to fill your Daily Report.</p></Card>
  );
  if (!isMgr && !isExe) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v === "" ? 0 : Number(v) }));

  const save = async () => {
    if (isMgr && Number(form.took_treatment) > Number(form.total_counselled))
      toast.warning("Check: Took Treatment ≤ Total Counselled");
    if (isExe && Number(form.due_recovered || 0) > Number(form.due_pending || 0))
      toast.warning("Recovered is greater than Due Pending — please verify");
    if (!workDate || workDate > today) { toast.error("Future date not allowed"); return; }
    const body = { ...form, person, role, branch, work_date: workDate, entered_by: person };
    try {
      await api.post("/dailywork/upsert", body);
      toast.success(`Daily Report saved for ${workDate}`);
      load();
    } catch { toast.error("Save failed"); }
  };

  const lfSum = sumF(form, MGR_LF);
  const fbSum = sumF(form, MGR_FB);

  return (
    <Card className="p-5 mb-6" data-testid="dailywork-card">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <ClipboardList className="h-4 w-4 text-[#C9A24B]" />
          <span className="text-sm text-slate-200">{isMgr ? "Manager Daily Report" : "Daily Work"} — <span className="text-[#C9A24B]">{person}</span> <span className="text-slate-500">({role}){branch ? ` · ${branch}` : ""}</span></span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="text-[11px] text-slate-400">Date</label>
          <input data-testid="dailywork-date" type="date" max={today} value={workDate} onChange={(e) => setWorkDate(e.target.value || today)}
            className="h-9 rounded-md bg-[#0F172A] border border-white/10 text-white text-sm px-2 [color-scheme:dark]" />
          <span data-testid="dailywork-status" className="text-[11px] text-slate-500">{editingId ? "Saved entry loaded" : "New entry"}</span>
          <Button data-testid="dailywork-save" onClick={save} className="bg-[#C9A24B] text-[#0F172A] hover:bg-[#b8923f]">
            <Save className="h-4 w-4 mr-2" /> Save
          </Button>
        </div>
      </div>

      {isExe && (
        <>
          <NumGrid list={EXE} form={form} set={set} />
          <p className="text-[11px] uppercase tracking-wider text-slate-500 mt-4 mb-2">Patient Satisfaction</p>
          <NumGrid list={EXE_SAT} form={form} set={set} />
          <div className="flex flex-wrap gap-5 mt-4 text-sm">
            <span className="text-slate-400">Connect Rate: <span className="text-[#34d399] font-mono-ync">{pct(form.calls_connected, form.calls_made)}%</span></span>
            {(() => { const tot = (Number(form.happy) || 0) + (Number(form.satisfied) || 0) + (Number(form.unhappy) || 0);
              return (<>
                <span className="text-slate-400">Total Responded: <span className="text-white font-mono-ync">{tot}</span></span>
                <span className="text-slate-400">Happy %: <span className="text-[#34d399] font-mono-ync">{pct(form.happy, tot)}%</span></span>
                <span className="text-slate-400">Satisfied %: <span className="text-[#C9A24B] font-mono-ync">{pct(form.satisfied, tot)}%</span></span>
                <span className="text-slate-400">Unhappy %: <span className="text-[#f87171] font-mono-ync">{pct(form.unhappy, tot)}%</span></span>
              </>); })()}
          </div>
          <div className="flex flex-wrap items-center gap-3 mt-4">
            <UnhappyButton onSaved={load} />
            {unhappyReasons < (Number(form.unhappy) || 0) && (
              <span className="text-xs text-[#C9A24B]" data-testid="unhappy-hint">Hint: {(Number(form.unhappy) || 0) - unhappyReasons} unhappy patient(s) without a reason logged</span>
            )}
          </div>
        </>
      )}

      {isMgr && (
        <>
          <NumGrid list={MGR_COUNTS} form={form} set={set} />
          <p className="text-[11px] uppercase tracking-wider text-slate-500 mt-4 mb-2">Look & Feel (0–5)</p>
          <NumGrid list={MGR_LF} form={form} set={set} max={5} />
          <p className="text-[11px] uppercase tracking-wider text-slate-500 mt-4 mb-2">Feedback (1–5)</p>
          <NumGrid list={MGR_FB} form={form} set={set} max={5} />
          <p className="text-[11px] uppercase tracking-wider text-slate-500 mt-4 mb-2">Suggestions</p>
          <NumGrid list={MGR_SUG} form={form} set={set} />
          <div className="flex flex-wrap gap-5 mt-4 text-sm">
            <span className="text-slate-400">Conversion %: <span className="text-[#34d399] font-mono-ync">{pct(form.took_treatment, form.total_counselled)}%</span></span>
            <span className="text-slate-400">Look & Feel %: <span className="text-[#C9A24B] font-mono-ync">{Math.round((lfSum / 30) * 100)}%</span></span>
            <span className="text-slate-400">Feedback Score %: <span className="text-[#34d399] font-mono-ync">{Math.round(((fbSum / 5) / 5) * 100)}%</span></span>
          </div>
          <div className="flex flex-wrap gap-5 mt-2 text-sm" data-testid="mgr-complaints">
            <span className="text-slate-400">Complaints — Received <span className="font-mono-ync text-white">{complaints.received}</span> · Resolved <span className="font-mono-ync text-[#34d399]">{complaints.resolved}</span> · Open <span className="font-mono-ync text-[#f87171]">{complaints.open}</span> <span className="text-slate-600">(from Concerns{branch ? ` · ${branch}` : ""})</span></span>
          </div>
          <div className="flex flex-wrap items-center gap-3 mt-4">
            <ComplaintButton onSaved={load} />
            <SuggestionButton onSaved={load} />
          </div>
        </>
      )}
    </Card>
  );
}
